<div class="fullcontainer">
  <br />
  <br />

  <div class='titleContainer'>
    <h2 class='header' style='color: #006498'>Warehousing</h2>

    <div>
      <button class='btn btn-secondary' routerLink='/warehousing/goods-in'><i class="fas fa-dolly"></i> Goods In</button>&nbsp;
      <button class='btn btn-secondary' routerLink='/warehousing/goods-out'><i class="fas fa-dolly-flatbed"></i> Goods Out</button>&nbsp;
      <button class='btn btn-secondary' routerLink='/warehousing/transfer'><i class="fas fa-exchange-alt"></i> Transfer</button>&nbsp;
      <button class='btn btn-secondary' routerLink='/warehousing/adjustment'><i class="fas fa-sliders-h"></i> Adjustment</button>&nbsp;
      <button class='btn btn-secondary' routerLink='/warehousing/reports'><i class="fas fa-chart-line"></i> Reports</button>&nbsp;
      <button class='btn btn-secondary' routerLink='/warehousing/storage-rates'><i class="fas fa-pound-sign"></i> Charge Rates</button>&nbsp;
      <button class='btn btn-secondary' (click)='this.loadDashboard()'><i class="fas fa-sync"></i> Refresh</button>&nbsp;
    </div>
  </div>
  <br />

  @if (this.loading) {
    <div class='sheet'>
      <p class='m-0'><i class="fas fa-spinner fa-spin"></i> Loading warehousing figures&hellip;</p>
    </div>
  }

  @if (this.loadFailed) {
    <div class='alert alert-danger'>
      <strong>The warehousing figures could not be loaded.</strong>
      No total on this screen can be trusted while this message is showing.
      <button class='btn btn-sm btn-danger ml-2' (click)='this.loadDashboard()'>Try again</button>
    </div>
  }

  @if (!this.loading && !this.loadFailed) {

    <!-- Negative stock sits above everything else on purpose. It means the ledger
         disagrees with itself, so it must not be something you scroll to find. -->
    @if (this.negativeCount > 0) {
      <div class='alert alert-danger'>
        <div class='titleContainer'>
          <h4 class='mb-0'>
            <i class="fas fa-exclamation-triangle"></i>
            {{ this.negativeCount }} stock level{{ this.negativeCount === 1 ? '' : 's' }} negative
          </h4>
          <button class='btn btn-sm btn-outline-dark' routerLink='/warehousing/reports/reconciliation'>
            Reconcile against the ledger
          </button>
        </div>
        <hr />
        <p>
          A level is the sum of an append-only ledger, so it cannot legitimately fall below
          zero. Each row below means movements are missing or were posted with the wrong sign.
        </p>
        <table class='table table-sm mb-0'>
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Warehouse</th>
              <th>Aisle</th>
              <th>Shelf</th>
              <th class='text-right'>Qty</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (row of this.visibleNegative; track row.productStockLevelId) {
              <tr>
                <td>{{ row.productName }}</td>
                <td>{{ row.sku }}</td>
                <td>{{ row.warehouseName }}</td>
                <td>{{ row.aisle }}</td>
                <td>{{ row.shelf }}</td>
                <td class='text-right'><strong>{{ this.formatQty(row.qty) }}</strong></td>
                <td>
                  <button class='btn btn-sm btn-warning'
                    [routerLink]="'/warehousing/reports/movement-history'"
                    [queryParams]='{productId: row.productId}'>Audit trail</button>
                </td>
              </tr>
            }
          </tbody>
        </table>
        @if (this.negativeCount > this.exceptionPreview) {
          <button class='btn btn-sm btn-link' (click)='this.showAllNegative = !this.showAllNegative'>
            {{ this.showAllNegative ? 'Show fewer' : 'Show all ' + this.negativeCount }}
          </button>
        }
      </div>
    }

    <div class='row'>
      <div class='col-md-3'>
        <div class='sheet mt-0'>
          <h6 class='text-muted mb-1'>Total Stock Qty</h6>
          <h2 class='header mb-0'>{{ this.formatQty(this.dashboard.kpis.totalStockQty) }}</h2>
        </div>
      </div>
      <div class='col-md-3'>
        <div class='sheet mt-0'>
          <h6 class='text-muted mb-1'>Products Holding Stock</h6>
          <h2 class='header mb-0'>{{ this.dashboard.kpis.productsHoldingStock }}</h2>
        </div>
      </div>
      <div class='col-md-3'>
        <div class='sheet mt-0'>
          <h6 class='text-muted mb-1'>Locations In Use</h6>
          <h2 class='header mb-0'>{{ this.dashboard.kpis.locationsInUse }}</h2>
        </div>
      </div>
      <div class='col-md-3'>
        <div class='sheet mt-0'>
          <h6 class='text-muted mb-1'>Movements Today</h6>
          <h2 class='header mb-0'>{{ this.dashboard.kpis.movementsToday }}</h2>
        </div>
      </div>
    </div>

    <!-- Ledger health. A discrepancy means a stored level disagrees with the ledger behind
         it, which is a defect rather than something to watch — the tolerance is already
         applied server-side, so this is never rounding noise. -->
    @if (this.dashboard.summaryLoaded && this.reconciliationDiscrepancies > 0) {
      <div class='alert alert-danger'>
        <strong>
          <i class="fas fa-exclamation-triangle"></i>
          {{ this.reconciliationDiscrepancies }} stock level(s) disagree with the ledger.
        </strong>
        The level is a cache of the ledger, not an independent number, so it is the level
        that is wrong.
        <button class='btn btn-sm btn-outline-dark ml-2' routerLink='/warehousing/reports/reconciliation'>
          Run the reconciliation report
        </button>
      </div>
    }

    <!-- Stock at cost. Deliberately NOT "stock value" or "asset value": much of this
         warehouse is 3PL and the goods belong to Blacks Haulage's customers, not to Blacks
         Haulage. The coverage line is not optional — see valuationIncomplete. -->
    <div class='sheet'>
      <div class='titleContainer'>
        <h4 class='header'>Stock at Cost</h4>
        <button class='btn btn-sm btn-secondary' routerLink='/warehousing/reports/stock-on-hand'>
          Stock on Hand
        </button>
      </div>

      @if (!this.dashboard.summaryLoaded) {
        <div class='alert alert-warning mb-0'>
          <strong>The valuation could not be loaded.</strong>
          No figure is shown rather than a zero, because zero and "not known" are different
          facts and only one of them is safe to act on.
        </div>
      }

      @if (this.dashboard.summaryLoaded) {
        <div class='row'>
          <div class='col-md-4'>
            <h2 class='header mb-1'>{{ this.formatMoney(this.dashboard.summary.valuation.value) }}</h2>
            <p class='text-muted mb-0'>
              <small>
                <!-- Denominator is the valuation's OWN unitsOnHand, not summary.totalQty. The four
                     coverage buckets are defined to divide up unitsOnHand exactly; totalQty is the
                     sum of the stored levels, and the two differ precisely when the levels have
                     drifted from the ledger - the case reconciliation exists to find. Using
                     totalQty makes the fraction stop adding up in exactly the situation the reader
                     most needs it to. -->
                across {{ this.formatQty(this.dashboard.summary.valuation.valuedUnits) }} unit(s)
                of {{ this.formatQty(this.dashboard.summary.valuation.unitsOnHand) }} on hand
              </small>
            </p>
          </div>

          <div class='col-md-8'>
            <!-- Nothing priced at all: the number is not a partial figure, it is no figure. -->
            @if (this.valuationCoversNothing) {
              <div class='alert alert-danger mb-0'>
                <strong>This figure covers none of the stock.</strong>
                {{ this.formatQty(this.dashboard.summary.valuation.unitsWithoutCostBasis) }} unit(s)
                across {{ this.dashboard.summary.valuation.productsWithoutCostBasis }} product(s)
                came in on movements carrying no unit cost, so the total above is not a
                valuation of anything.
                <br>
                <!-- Do NOT shorten this to "record a cost". A cost is snapshotted onto the
                     movement at posting and never updated, so setting one on the product
                     screen prices the next intake and leaves this figure at zero - the
                     reader would do as they were told, see no change, and report a bug. -->
                Setting a cost on the product screen prices the <strong>next</strong> intake;
                it does not restate stock already received. To value what is on the shelf
                now, post a <strong>revaluation</strong>.
              </div>
            }

            <!-- Its own alert, not a bullet in the coverage list below: every other note here says
                 the total leaves stock out, this one says the total is wrong about stock it did
                 include. A negative cost cannot be produced by ordinary trading - only by writing
                 more off a product than it was ever worth - so it is an operator error to correct,
                 not a gap to explain. -->
            @if (this.valuationHasNegativeValue) {
              <div class='alert alert-danger mb-2'>
                <strong>Some of this money is negative, which means it is wrong.</strong>
                <ul class='mb-0'>
                  @if (this.dashboard.summary.valuation.unitsAtNegativeCost > 0) {
                    <li>
                      {{ this.formatQty(this.dashboard.summary.valuation.unitsAtNegativeCost) }} unit(s)
                      across {{ this.dashboard.summary.valuation.productsAtNegativeCost }} product(s)
                      are on the shelf at a <strong>negative cost</strong>, holding
                      {{ this.formatMoney(this.dashboard.summary.valuation.negativeCostValueExcluded) }}.
                      Kept out of the total above rather than deducted from it — netted off, it would
                      cancel good stock in another aisle and leave a total that still looked right.
                    </li>
                  }
                  @if (this.dashboard.summary.valuation.negativeUnits > 0) {
                    <li>
                      {{ this.formatQty(this.dashboard.summary.valuation.negativeUnits) }} unit(s) are at a
                      <strong>negative quantity</strong> — stock booked out that was never booked in.
                      <!-- Nested, not a sibling bullet, because these units are a SUBSET of the line
                           above and two bullets side by side read as two separate quantities to be
                           added. The backend keeps them in `negativeUnits` for the same reason: how
                           much stock is missing has one answer whatever it was priced at. -->
                      @if (this.dashboard.summary.valuation.negativeUnitsAtNegativeCost > 0) {
                        <ul class='mb-0'>
                          <li>
                            Of those,
                            {{ this.formatQty(this.dashboard.summary.valuation.negativeUnitsAtNegativeCost) }}
                            unit(s) are also at a <strong>negative cost</strong>, holding
                            {{ this.formatMoney(this.dashboard.summary.valuation.negativeValueExcludedAtNegativeCost) }}.
                            Two faults on one holding: the ledger is short, and the value has been
                            written down past nothing. Reported separately so it cannot cancel the
                            money at risk on stock that is merely missing.
                          </li>
                        </ul>
                      }
                    </li>
                  }
                  @if (this.dashboard.summary.valuation.negativeValueProducts > 0) {
                    <li>
                      {{ this.dashboard.summary.valuation.negativeValueProducts }} product(s) value
                      negative across their whole ledger.
                    </li>
                  }
                </ul>
                <p class='mb-0 mt-2'>
                  <small>Post a revaluation to correct the product's value.</small>
                </p>
              </div>
            }

            @if (this.valuationHasStrandedValue) {
              <div class='alert alert-warning mb-0'>
                <strong>
                  {{ this.formatMoney(this.dashboard.summary.valuation.strandedValueAdjustment) }}
                  of revaluation is not in this total.
                </strong>
                <p class='mb-0 mt-2'>
                  It was posted against product(s) with no costed intake, so there was no quantity to
                  divide it into and it did not move any unit cost. The amount was accepted and
                  recorded, and it is stranded — correcting it needs stock booked in at a cost, then
                  the revaluation posting again.
                </p>
              </div>
            }

            @if (!this.valuationCoversNothing && this.valuationCoverageIncomplete) {
              <div class='alert alert-warning mb-0'>
                <strong>This figure covers part of the warehouse.</strong>
                <ul class='mb-0'>
                  @if (this.dashboard.summary.valuation.unitsWithoutCostBasis > 0) {
                    <li>
                      {{ this.formatQty(this.dashboard.summary.valuation.unitsWithoutCostBasis) }} unit(s)
                      across {{ this.dashboard.summary.valuation.productsWithoutCostBasis }} product(s)
                      have <strong>no cost basis</strong> - nothing that put them there
                      carried a unit cost - and are excluded.
                    </li>
                  }
                  @if (this.dashboard.summary.valuation.unitsAtZeroCost > 0) {
                    <li>
                      <!-- Says what is known and stops. The flag behind this is a cost of exactly
                           zero, and a product written off entirely reaches that same zero, so the
                           old copy - "third-party stock the customer owns" - told Blacks Haulage
                           their own written-off stock belonged to somebody else. Nothing on this
                           payload separates the two, so nothing here may imply which it is. -->
                      {{ this.formatQty(this.dashboard.summary.valuation.unitsAtZeroCost) }} unit(s)
                      across {{ this.dashboard.summary.valuation.productsAtZeroCost }} product(s)
                      are <strong>priced at zero</strong>. Counted, and contributing nothing to the
                      total. Zero is deliberate for third-party stock a customer owns, and is also
                      where a fully written-off product lands — check the product before reading it
                      as either.
                    </li>
                  }
                  @if (this.dashboard.summary.valuation.unitsFromUncostedMovements > 0) {
                    <li>
                      <!-- Not "before costs were recorded", which reads as a one-off migration
                           artefact that will age out. A new product is created with no cost at
                           all, so anything booked in before somebody prices it lands here. This
                           is an ongoing condition and the wording has to survive the day the
                           historic data is gone. -->
                      {{ this.formatQty(this.dashboard.summary.valuation.unitsFromUncostedMovements) }} unit(s)
                      were booked in on movements carrying no unit cost, so the average they sit
                      behind rests on less stock than it appears to.
                    </li>
                  }
                </ul>
              </div>
            }

            @if (!this.valuationIncomplete) {
              <p class='text-muted mb-0'>
                Every unit on hand has a cost basis, so this figure covers the whole warehouse.
                It is stock at cost, not an asset value — third-party stock is included at the
                zero cost it is correctly held at.
              </p>
            }
          </div>
        </div>
      }
    </div>

    <div class='row'>
      <div class='col-md-7'>
        <div class='sheet'>
          <div class='titleContainer'>
            <h4 class='header'>Occupancy by Warehouse</h4>
            <button class='btn btn-sm btn-secondary' routerLink='/warehousing/warehouses'>Warehouses</button>
          </div>
          <br />
          @if (this.dashboard.occupancy.length === 0) {
            <p class='text-muted mb-0'>No warehouses have been set up yet.</p>
          }
          @if (this.dashboard.occupancy.length > 0) {
            <table class='table'>
              <thead>
                <tr>
                  <th>Warehouse</th>
                  <th class='text-right'>Locations Used</th>
                  <th>Occupancy</th>
                  <th class='text-right'>Products</th>
                  <th class='text-right'>Total Qty</th>
                </tr>
              </thead>
              <tbody>
                @for (row of this.dashboard.occupancy; track row.warehouseId) {
                  <tr>
                    <td>
                      <a [routerLink]="'/warehousing/stock-levels'">{{ row.warehouseName }}</a>
                    </td>
                    <td class='text-right'>
                      {{ row.locationsInUse }} / {{ row.locationCount }}
                      <!-- Stock at a location that no longer exists. Reachable because the
                           schema has no foreign keys, so a level can outlive its location. -->
                      @if (row.orphanedLocations > 0) {
                        <i class="fas fa-exclamation-triangle text-danger"
                          [title]='row.orphanedLocations + " location(s) holding stock no longer exist"'></i>
                      }
                    </td>
                    <td>
                      <div class='progress' style='height: 1.1rem;'>
                        <div class='progress-bar bg-info' role='progressbar'
                          [style.width.%]='row.usedPercent'>{{ row.usedPercent }}%</div>
                      </div>
                      @if (row.orphanedLocations > 0) {
                        <small class='text-danger'>
                          {{ row.orphanedLocations }} location(s) holding stock no longer exist
                        </small>
                      }
                    </td>
                    <td class='text-right'>{{ row.productCount }}</td>
                    <!-- A site total can legitimately be negative while a level is wrong,
                         so it is flagged rather than quietly displayed. -->
                    <td class='text-right' [class.text-danger]='this.isNegative(row.totalQty)'>
                      {{ this.formatQty(row.totalQty) }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>
      </div>

      <div class='col-md-5'>
        <div class='sheet'>
          <div class='titleContainer'>
            <h4 class='header'>Empty Locations</h4>
            <button class='btn btn-sm btn-secondary' routerLink='/warehousing/reports/stock-on-hand'>Stock on Hand</button>
          </div>
          <br />
          @if (this.zeroCount === 0) {
            <p class='text-muted mb-0'>No stock level is sitting at zero.</p>
          }
          @if (this.zeroCount > 0) {
            <p class='text-muted'>
              {{ this.zeroCount }} level{{ this.zeroCount === 1 ? '' : 's' }} at zero. These are
              normal — the shelf has been emptied — but the row stays so the history survives.
            </p>
            <table class='table table-sm'>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Warehouse</th>
                  <th>Aisle</th>
                  <th>Shelf</th>
                </tr>
              </thead>
              <tbody>
                @for (row of this.visibleZero; track row.productStockLevelId) {
                  <tr>
                    <td>{{ row.productName }}</td>
                    <td>{{ row.warehouseName }}</td>
                    <td>{{ row.aisle }}</td>
                    <td>{{ row.shelf }}</td>
                  </tr>
                }
              </tbody>
            </table>
            @if (this.zeroCount > this.exceptionPreview) {
              <button class='btn btn-sm btn-link' (click)='this.showAllZero = !this.showAllZero'>
                {{ this.showAllZero ? 'Show fewer' : 'Show all ' + this.zeroCount }}
              </button>
            }
          }
        </div>
      </div>
    </div>

    <!-- Charts live in their own partial so this template stays under the repo's file
         length limit, and so NgxChartsModule is needed by one component rather than the
         whole module. The partial only draws: the chart service already decided which
         forms the data can honestly support. -->
    <app-warehousing-charts [dashboard]='this.dashboard'></app-warehousing-charts>

    <div class='sheet'>
      <div class='titleContainer'>
        <h4 class='header'>Recent Movements</h4>
        <button class='btn btn-sm btn-secondary' routerLink='/warehousing/stock-movements'>
          <i class="fas fa-history"></i> Full Movement History
        </button>
      </div>
      <br />
      @if (this.dashboard.recentMovements.length === 0) {
        <p class='text-muted mb-0'>No stock has been moved yet.</p>
      }
      @if (this.dashboard.recentMovements.length > 0) {
        <table class='table'>
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Product</th>
              <th>SKU</th>
              <th>Warehouse</th>
              <th>Aisle</th>
              <th>Shelf</th>
              <th>Type</th>
              <th class='text-right'>Qty</th>
              <th class='text-right'>Start Weight</th>
              <th class='text-right'>End Weight</th>
              <th class='text-right'>Net Weight</th>
              <th>Reference</th>
            </tr>
          </thead>
          <tbody>
            @for (movement of this.dashboard.recentMovements; track movement.id) {
              <tr>
                <td>{{ movement.id }}</td>
                <td>{{ this.formatDateTime(movement.createdAt) }}</td>
                <td>{{ movement.productName }}</td>
                <td>{{ movement.sku }}</td>
                <td>{{ movement.warehouseName }}</td>
                <td>{{ movement.aisle }}</td>
                <td>{{ movement.shelf }}</td>
                <td>{{ movement.movementTypeLabel }}</td>
                <!-- A movement qty is signed, so a negative here is normal, not a defect. -->
                <td class='text-right'>{{ this.formatQty(movement.qty) }}</td>
                <!-- Blank where the goods were not weighed, which is most of them, and blank on the
                     net where only one reading was taken. This feed totals nothing — it is the last
                     twenty movements rather than a report — so there is no net-weight figure here
                     and therefore no coverage count to put beside one. The movement history report
                     is where a weight total and its unweighed count are drawn. -->
                <td class='text-right'>{{ this.formatWeight(movement.startWeight) }}</td>
                <td class='text-right'>{{ this.formatWeight(movement.endWeight) }}</td>
                <td class='text-right'>{{ this.formatNetWeight(movement) }}</td>
                <td>{{ movement.referenceName }}</td>
              </tr>
            }
          </tbody>
        </table>
      }
    </div>
  }
</div>
